import React, { useState, FC, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Tabs from "../../components/ui/Tabs";
import {
  ApplicationData,
  BadgeProps,
  StatusConfig,
  TabItem,
} from "../../types/components";
import jobService from "../../services/job.service";
import { useAuth } from "../../hooks/useAuth";
import { JobApplicationStatus } from "../../types/api";

const UserApplications: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("all");
  const [applications, setApplications] = useState<ApplicationData[]>([]);

  useEffect(() => {
    if (user) {
      jobService
        .getUserApplications(user.userId, 1, 10)
        .then((response) => {
          if (response.data.items) {
            setApplications(response.data.items);
          }
        })
        .catch((error) => {
          console.error("Error fetching applications:", error);
        });
    }
  }, [user]);

  const getStatusBadge = (status?: JobApplicationStatus) => {
    const statusConfig: { [key in JobApplicationStatus]: StatusConfig } = {
      Invited: { variant: "info", label: "Invited" },
      Bookmarked: { variant: "info", label: "Bookmarked" },
      ViewedByCandidate: { variant: "info", label: "Viewed" },
      ViewedByEmployer: { variant: "info", label: "Viewed by Employer" },
      NotInterested: { variant: "secondary", label: "Not Interested" },
      Applied: { variant: "primary", label: "Applied" },
      WithdrawnByCandidate: {
        variant: "secondary",
        label: "Withdrawn",
      },
      UnderReview: { variant: "warning", label: "Under Review" },
      Shortlisted: { variant: "success", label: "Shortlisted" },
      ReachedOut: { variant: "info", label: "Reached Out" },
      InterviewScheduled: {
        variant: "info",
        label: "Interview Scheduled",
      },
      InterviewNo_Show: {
        variant: "danger",
        label: "Interview No Show",
      },
      InterviewCompleted: {
        variant: "success",
        label: "Interview Completed",
      },
      Rejected: { variant: "danger", label: "Rejected" },
      Offered: { variant: "success", label: "Offer Received" },
      OfferedAccepted: {
        variant: "success",
        label: "Offer Accepted",
      },
      OfferDeclined: {
        variant: "danger",
        label: "Offer Declined",
      },
      Hired: { variant: "success", label: "Hired" },
      OnHold: { variant: "warning", label: "On Hold" },
      Waitlisted: { variant: "warning", label: "Waitlisted" },
    };

    const config = status
      ? statusConfig[status]
      : { variant: "secondary", label: "Unknown" };
    return (
      <Badge variant={config.variant as BadgeProps["variant"]}>
        {config.label}
      </Badge>
    );
  };

  const getStatusColor = (status?: JobApplicationStatus): string => {
    const colors: { [key in JobApplicationStatus]: string } = {
      Invited: "border-blue-200 bg-blue-50",
      Bookmarked: "border-blue-200 bg-blue-50",
      ViewedByCandidate: "border-blue-200 bg-blue-50",
      ViewedByEmployer: "border-blue-200 bg-blue-50",
      NotInterested: "border-gray-200 bg-gray-50",
      Applied: "border-primary-lightGreen/20 bg-primary-lightGreen/10",
      WithdrawnByCandidate: "border-gray-200 bg-gray-50",
      UnderReview: "border-yellow-200 bg-yellow-50",
      Shortlisted: "border-green-200 bg-green-50",
      ReachedOut: "border-blue-200 bg-blue-50",
      InterviewScheduled:
        "border-primary-lightGreen/20 bg-primary-lightGreen/10",
      InterviewNo_Show: "border-red-200 bg-red-50",
      InterviewCompleted: "border-green-200 bg-green-50",
      Rejected: "border-red-200 bg-red-50",
      Offered: "border-green-200 bg-green-50",
      OfferedAccepted: "border-green-200 bg-green-50",
      OfferDeclined: "border-red-200 bg-red-50",
      Hired: "border-green-200 bg-green-50",
      OnHold: "border-yellow-200 bg-yellow-50",
      Waitlisted: "border-yellow-200 bg-yellow-50",
    };
    return status ? colors[status] : "border-gray-200 bg-gray-50";
  };

  const filteredApplications = applications.filter((app) => {
    if (activeTab === "all") return true;
    return app.status === activeTab;
  });

  const tabs: TabItem[] = [
    {
      id: "all",
      label: "All Applications",
      content: null,
      badge: applications.length,
    },
    {
      id: "UnderReview",
      label: "Under Review",
      content: null,
      badge: applications.filter((app) => app.status === "UnderReview").length,
    },
    {
      id: "InterviewScheduled",
      label: "Interviews",
      content: null,
      badge: applications.filter((app) => app.status === "InterviewScheduled")
        .length,
    },
    {
      id: "Offered",
      label: "Offers",
      content: null,
      badge: applications.filter((app) => app.status === "Offered").length,
    },
    {
      id: "Rejected",
      label: "Not Selected",
      content: null,
      badge: applications.filter((app) => app.status === "Rejected").length,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary-black">
            Application Status
          </h1>
          <p className="text-gray-600 mt-2">
            Track the progress of your job applications
          </p>
        </div>
        {/* <div className="mt-4 md:mt-0">
          <Button variant="outline">
            Export Applications
          </Button>
        </div> */}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Applications
              </p>
              <p className="text-2xl font-bold text-primary-black">
                {applications.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-lightGreen rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-primary-black"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Under Review</p>
              <p className="text-2xl font-bold text-yellow-600">
                {
                  applications.filter((app) => app.status === "UnderReview")
                    .length
                }
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Interviews</p>
              <p className="text-2xl font-bold text-primary-lightGreen">
                {
                  applications.filter(
                    (app) => app.status === "InterviewScheduled",
                  ).length
                }
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-lightGreen/20 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-primary-lightGreen"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Offers</p>
              <p className="text-2xl font-bold text-green-600">
                {applications.filter((app) => app.status === "Offered").length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl p-6 shadow-soft">
        <Tabs
          tabs={tabs}
          defaultTab={tabs.findIndex((tab) => tab.id === activeTab)}
          onTabChange={(index) => setActiveTab(tabs[index].id)}
        />
      </div>

      {/* Applications List */}
      <div className="space-y-6">
        {filteredApplications.length > 0 ? (
          filteredApplications.map((application) => (
            <div
              key={application.applicationId}
              className={`bg-white rounded-2xl p-6 shadow-soft border-l-4 ${getStatusColor(
                application.status,
              )}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-primary-black mb-2">
                        {application.jobTitle}
                      </h3>
                      <p className="text-lg text-gray-600 mb-2">
                        {application.location != null ? (
                          <>
                            {application.companyName} • {application.location}
                          </>
                        ) : (
                          <>{application.companyName}</>
                        )}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{application.jobType}</span>
                        <span>•</span>
                        <span>
                          {application.minSalary?.toLocaleString()}
                          {application.maxSalary &&
                            ` - ${application.maxSalary.toLocaleString()}`}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      {getStatusBadge(application.status)}
                      <span className="text-xs text-gray-500">
                        Applied{" "}
                        {new Date(
                          application.createdDate || "",
                        ).toLocaleDateString()}
                      </span>
                      <Link to={`/user/post/${application.postId}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          // onClick={() => navigate(`user/post/${application.postId}`)}
                          >
                          View Post
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Latest Update:</strong> {application.notes}
                    </p>
                    <p className="text-xs text-gray-500">
                      Last updated:{" "}
                      {new Date(
                        application.lastUpdate || ""
                      ).toLocaleDateString()}
                    </p>
                  </div> */}
                </div>

              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No applications found
            </h3>
            <p className="text-gray-500 mb-4">
              {activeTab === "all"
                ? "You haven't applied to any jobs yet."
                : `No applications with status "${activeTab}" found.`}
            </p>
            {/* <Button>
              Browse Jobs
            </Button> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserApplications;
